import { FinanceFactSummariesQuery, InsightTableItemsQuery } from 'generated/resources/graphql'

import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'
import {
  groupChildrenByCategory,
  groupRecordsByCategory,
  mergeComparisonDataByIds,
} from 'modules/finance/components/FinancialWorkspaceDataCollection/utils/transformData'

import { transformItemToRecord } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/transformRecords'

/**
 * Transforms FinanceFactSummaries query data with category grouping
 *
 * Children are always pre-loaded from the backend for Compensation tables.
 *
 * When groupBy is LineItem: Group flat items by categoryLabel (e.g., "Earnings", "Deductions")
 * When groupBy is other (Employee, LegalEntity, etc.): Group children by categoryLabel
 *   - Expanding a parent shows category groups (Earnings, Deductions, Taxes)
 *   - Expanding a category shows individual line items
 *
 * @param comparisonMap - Optional map of comparison records by ID for merging comparison values
 */
export const mapFinanceFactSummariesData = (
  data: FinanceFactSummariesQuery,
  groupBy: InsightTableGroupBy,
  comparisonMap?: Map<string | number, FinancialWorkspaceRecord>
): FinancialWorkspaceRecord[] => {
  const edges = data.financialWorkspace.financeFactSummariesConnection.edges
  let records = edges.map((edge) =>
    transformItemToRecord({
      id: edge.node.id,
      label: edge.node.label,
      rows: edge.node.rows,
      children: edge.node.children,
      categoryLabel: edge.node.categoryLabel,
      category: edge.node.category,
      active: edge.node.active,
      imageUrl: edge.node.imageUrl,
      hasChildren: edge.node.hasChildren,
    })
  )

  // Merge comparison data if available
  if (comparisonMap && comparisonMap.size > 0) {
    const comparisonRecords = Array.from(comparisonMap.values())
    records = mergeComparisonDataByIds(records, comparisonRecords)
  }

  // Apply category grouping based on groupBy
  if (groupBy === InsightTableGroupBy.LineItem) {
    // For LineItem view, group the flat list of items by categoryLabel
    return groupRecordsByCategory(records)
  } else {
    // For other views (Employee, LegalEntity, etc.), group the children of each record
    // This creates nested structure: Parent → Category → LineItems
    return groupChildrenByCategory(records)
  }
}

/**
 * Transforms InsightTableItems query data with category grouping
 *
 * When groupBy is LineItem: Group flat items by categoryLabel
 * When groupBy is other (Employee, LegalEntity, etc.): Group children by categoryLabel
 *
 * @param comparisonMap - Optional map of comparison records by ID for merging comparison values
 */
export const mapInsightTableItemsData = (
  data: InsightTableItemsQuery,
  groupBy: InsightTableGroupBy,
  comparisonMap?: Map<string | number, FinancialWorkspaceRecord>
): FinancialWorkspaceRecord[] => {
  const edges = data.financialWorkspace.insightTableItemsConnection.edges
  let records = edges.map((edge) =>
    transformItemToRecord({
      id: edge.node.id,
      label: edge.node.label,
      rows: edge.node.rows,
      children: edge.node.children,
      categoryLabel: edge.node.categoryLabel,
      category: edge.node.category,
      active: edge.node.active,
      imageUrl: edge.node.imageUrl,
    })
  )

  // Merge comparison data if available
  if (comparisonMap && comparisonMap.size > 0) {
    const comparisonRecords = Array.from(comparisonMap.values())
    records = mergeComparisonDataByIds(records, comparisonRecords)
  }

  // Apply category grouping based on groupBy
  if (groupBy === InsightTableGroupBy.LineItem) {
    // For LineItem view, group the flat list of items by categoryLabel
    return groupRecordsByCategory(records)
  } else {
    // For other views (Employee, LegalEntity, etc.), group the children of each record
    return groupChildrenByCategory(records)
  }
}
